import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { type FilterOptionsState } from '@mui/material/useAutocomplete';
import { ReactComponent as CampaignIcon } from '@/assets/images/icons/campaigns.svg';
import { ReactComponent as ConstituentIcon } from '@/assets/images/icons/constituents.svg';
import { ReactComponent as ContributionIcon } from '@/assets/images/icons/contribution.svg';
import translate from '@/lib/intl/translate';
import MatchingHeaderCategory from '../../constants/MatchingHeaderCategory';
import {
  MatchType,
  type APIHeader,
} from '../../lib/matchingTableHeadersConstants';
import type CSVHeaderColumn from '../../types/CSVHeaderColumn';
import type MatchingTableHeader from '../../types/MatchingTableHeader';
import { useMatchHeadersContext } from './MatchHeadersProvider';

const CATEGORY_VALUES: string[] = Object.values(MatchingHeaderCategory);
const AUTOCOMPLETE_CLASSES = {
  groupLabel: 'font-normal text-xs leading-5 px-3 pt-2 pb-1 text-black',
  option: 'p-3 focus:bg-grey-100 hover:bg-grey-100',
};

const filterOptions = createFilterOptions<MatchingTableHeader>();

type MatchHeadersAutocompleteProps = {
  header: CSVHeaderColumn;
};

export default function MatchHeadersAutocomplete({
  header,
}: MatchHeadersAutocompleteProps) {
  const { updateHeaders, headerOptions } = useMatchHeadersContext();
  const [value, setValue] = React.useState<null | MatchingTableHeader>(null);

  const handleChange = React.useCallback(
    (_: React.SyntheticEvent<Element>, newVal: unknown) => {
      const newValue = newVal ? (newVal as MatchingTableHeader) : null;
      setValue(newValue);

      updateHeaders(
        (newValue?.fieldName || value?.fieldName) as APIHeader,
        header,
        newValue ? MatchType.MATCHED : MatchType.UNMATCHED,
      );
    },
    [header, updateHeaders, value],
  );

  /**
   * This allows for searching for "campaign", "contribution", or "constituent"
   * and getting back all headers that match that category.
   */
  const handleFilterOptions = React.useCallback(
    (
      options: MatchingTableHeader[],
      state: FilterOptionsState<MatchingTableHeader>,
    ) => {
      const filtered = filterOptions(options, state);
      const trimmedValue = state.inputValue.trim();

      if (trimmedValue && CATEGORY_VALUES.includes(trimmedValue)) {
        return headerOptions.filter((opt) => opt.category === trimmedValue);
      }

      return filtered;
    },
    [headerOptions],
  );

  return (
    <Autocomplete
      classes={AUTOCOMPLETE_CLASSES}
      filterOptions={handleFilterOptions}
      limitTags={1}
      getOptionLabel={(option) =>
        typeof option.label === 'object' ? option.label.text : option.label
      }
      groupBy={(option) =>
        translate(`DATA_UPLOADS_CATEGORY_${option.category.toUpperCase()}`)
      }
      onChange={handleChange}
      options={headerOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate('DATA_UPLOADS_HEADER_TITLE')}
          placeholder={translate('DATA_UPLOADS_HEADER_TITLE')}
        />
      )}
      renderOption={(props, option) => {
        let child;
        let icon;
        const iconClassName = '[&_*]:fill-text-main';

        switch (option.category) {
          case MatchingHeaderCategory.CAMPAIGN:
            icon = <CampaignIcon className={iconClassName} />;
            break;
          case MatchingHeaderCategory.CONSTITUENT:
            icon = <ConstituentIcon className={iconClassName} />;
            break;
          case MatchingHeaderCategory.CONTRIBUTON:
          default:
            icon = <ContributionIcon className={iconClassName} />;
            break;
        }

        if (typeof option.label === 'object') {
          child = (
            <>
              <span className="mx-2">{option.label.text}</span>
              <Tooltip
                placement="bottom"
                title={
                  <span className="type-body4">{option.label.tooltip}</span>
                }
              >
                <ErrorOutlineIcon fontSize="small" />
              </Tooltip>
            </>
          );
        } else {
          child = <span className="ml-2">{option.label}</span>;
        }

        return (
          <li {...props} key={option.fieldName}>
            {icon}
            {child}
          </li>
        );
      }}
    />
  );
}
